import { registerWithGlobalApp } from '@app/utils/app';
import { AutoClearingQueue } from '@app/utils/cached/auto-clearing-queue';
import { ID } from '@app/utils/common';
import { PortalError, throwPortalError } from '@app/utils/error/portal-error';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  merge,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscriber,
  Subscription,
} from 'rxjs';
import {
  catchError,
  delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  share,
  shareReplay,
  take,
  tap,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { loggerFor } from '../logger';
import {
  Cached,
  CachedOptions,
  CachedRememberingEntry,
  RetentionPolicy,
  Source,
  SourceDefinition,
} from './cached';

/**
 * Factory function for instantiating rxjs-based (in-memory) `Cached` implementation.
 * @param options for instantiation
 *
 * Note: Please use the general `cached` factory from `cached.ts` file.
 */
export function rxCached<T>(options: CachedOptions<T>): Cached<T> {
  if (options.retention === RetentionPolicy.Forever && options.clearDelay) {
    throw new Error('`RetentionsPolicy.Forever` and `clearDelay` make no sense together');
  }
  return new RxCached<T>(options);
}

/**
 * Clear cached instances that use RetentionPolicy.Subscriptions
 */
export function clearAllSubscriptionBasedRxCached(): void {
  RxCached.clearAll(RetentionPolicy.Subscriptions);
}

export function updateRxCachedLoggers(): void {
  RxCached.updateLoggers();
}

/**
 * RxJS based `Cached` implementation.
 */
class RxCached<T> implements Cached<T> {
  static instances: RxCached<any>[] = [];

  private static _recordEvents: boolean = JSON.parse(
    localStorage.getItem('__cached_recordEvents__') ?? 'false'
  );

  static get recordEvents(): boolean {
    return RxCached._recordEvents;
  }

  // noinspection JSUnusedGlobalSymbols: Used by cached devtools
  static set recordEvents(value: boolean) {
    RxCached._recordEvents = value;
    RxCached.instances.forEach((instance) => instance.updateLogger(instance.id));
  }

  static clearAll(retentionPolicy?: RetentionPolicy): void {
    if (retentionPolicy != null) {
      RxCached.instances.forEach((cached) => {
        if (cached.cacheRetention === retentionPolicy) {
          cached.clear();
        }
      });
    } else {
      RxCached.instances.forEach((cached) => cached.clear());
    }
  }

  /**
   * Because of our cached extension that can collect
   * logs, we need to manually update the loggers whenever
   * the log level of the common cached logger changes.
   */
  static updateLoggers(): void {
    RxCached.instances.forEach((cached) => {
      cached.updateLogger(cached.id);
    });
  }

  private static addInstance(instance: RxCached<any>): void {
    RxCached.instances.push(instance);
  }

  readonly data$: Observable<T>;
  readonly dataWithError$: () => Observable<T>;

  /**
   * Tracks the ID of `setTimeout()` for clearing the cache. Needed in order to stop the clearing
   * of the cache, if a new subscriber came before the timeout happened.
   */
  private cacheClearTimeoutID?: any;

  /**
   * How long the data should remain in the cache.
   */
  private readonly cacheRetention: RetentionPolicy;

  /**
   * The time delay for clearing the cache in case `RetentionPolicy.Subscribers` is in use.
   */
  private readonly clearDelay: number;

  /**
   * Indicator of whether the cache has been cleared.
   */
  private cleared = false;

  private _cleared$ = new Subject<void>();
  readonly cleared$: Observable<void> = this._cleared$.asObservable().pipe(share());

  /**
   * data producer function
   */
  private dataProducer: (params?: any) => Observable<T>;

  /**
   * The event stream that leads to changes of the cache.
   */
  private dataProducerEvents$ = new Subject<ProducerEvents<T>>();

  private _error$ = new BehaviorSubject<PortalError | null>(null);
  readonly error$ = this._error$.asObservable().pipe(shareReplay(1));

  private _loading$ = new BehaviorSubject<boolean>(true);
  readonly loading$ = this.subscriberWithCount(this._loading$.asObservable().pipe(shareReplay(1)));

  private _hasSubscriptions$ = new BehaviorSubject<boolean>(false);
  readonly hasSubscriptions$ = this._hasSubscriptions$
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay(1));

  get remembering$(): Observable<CachedRememberingEntry[]> | null {
    if (this.rememberQueue == null) {
      this.logger.error(
        'It is not possible to access remembering$ for this instance, it does not remember anything'
      );

      return null;
    }

    return this.rememberQueue.timeouts$.pipe(
      map((timeouts) => {
        return timeouts.map((timeout): CachedRememberingEntry => {
          return {
            active: timeout.timeoutId == null,
            id: timeout.id,
          };
        });
      })
    );
  }

  /**
   * List of skippable sources (a source value can be ignored if it was emitted by a source but)
   */
  private skippableSources: SourceDefinition<any>[] = [];

  /**
   * Reloads the data of this instance every x milliseconds after the last time data was received as
   * long as there is an active subscription.
   */
  private readonly reloadInterval: number;

  /**
   * If the `cached` has also cached sources then they can also be reloaded on automatic reload based on `reloadInterval`.
   * Default is `false`.
   */
  private readonly reloadSourcesOnReloadInterval: boolean;

  /**
   * Tracks the ID of `setTimeout()` for reloading the cache.
   */
  private reloadTimeoutID?: any;

  /**
   *
   * @private
   */
  private rememberQueue?: AutoClearingQueue<T>;

  /**
   * Remember the last source data, so it is available when we want to reload the current data
   * without reloading the sources.
   */
  private sourceData: any[] | null = null;

  private sourceClearSubscription: Subscription | null = null;

  /**
   * Active subscription to the source data.
   */
  private sourceDataSubscription: Subscription | null = null;

  /**
   * Active subscription to the source loading state.
   */
  private sourceLoadingSubscription: Subscription | null = null;

  /**
   * Sources from which this cached inherits its data.
   */
  private _sources?: Source<any>[];

  get sources(): RxCached<any>[] | undefined {
    return this._sources?.map((source) => ('cached' in source ? source.cached : source) as RxCached<any>);
  }

  /**
   * How many subscriptions currently exist (to data$ or loading$).
   */
  private subscriptionCount: number = 0;

  /**
   * Indicates that the sources are currently producing data and that this instance is waiting for
   * it to be emitted. For as long as a source is producing data, load and reload events are skipped.
   */
  private waitingForSources: boolean = false;

  /**
   * If true then this instance will update its loading state if the sources start loading.
   * Default false.
   */
  private watchSourcesLoadingState: boolean;

  private log: (message: string, ...params: any[]) => void;

  private logger = loggerFor('Cached');

  /**
   * Value that was produced before the current value. We need this value so we
   * can ignore sources if necessary.
   */
  private previousData: T | undefined = undefined;

  /**
   * Value that was produced last or undefined if no value has been produced or
   * the instance had an error.
   */
  private producedData: T | undefined = undefined;

  private _error: PortalError | null = null;

  get error(): PortalError | null {
    return this._error;
  }

  private _id: string | null = null;

  get id(): string {
    // return a copy of the current identity data
    return this._id;
  }

  private _loading = true;

  get loading(): boolean {
    return this._loading;
  }

  /**
   * Indicator of whether the producer function is currently running. This is not the same as
   * `_loading`, because loading is initially `true` and after the cache has been cleared.
   */
  private _producingData = false;

  get producingData(): boolean {
    return this._producingData;
  }

  /**
   * State of the sources:
   *  - initialize: sources have not been loaded
   *  - start-reload: sources have started to reload
   *  - reloading: sources are being reloaded
   *  - done: sources are loaded
   */
  private sourceLoadingState: 'initialize' | 'start-reload' | 'done' | 'reloading' = 'initialize';

  get hasActiveSource(): boolean {
    return this.sources?.some((source) => source.loading) ?? false;
  }

  get hasSubscriptions(): boolean {
    return this.subscriptionCount > 0;
  }

  get snapshot(): T | undefined {
    return this.producedData;
  }

  get state(): CachedState {
    let state: CachedState = CachedState.Empty;

    if (this.error != null) {
      state = CachedState.Errored;
    } else if (this.waitingForSources) {
      state = CachedState.WaitingForSources;
    } else if (this.producingData) {
      state = CachedState.Producing;
    } else if (this.producedData !== undefined) {
      state = CachedState.HasValue;
    }

    return state;
  }

  // noinspection JSUnusedLocalSymbols: This is required for the dev-tools to identify cached instances.
  private uuid = ID(2);

  constructor(options: CachedOptions<T>) {
    const {
      clearDelay = 10000,
      dataProducer,
      id,
      reloadInterval,
      reloadSourcesOnReloadInterval,
      rememberCount,
      rememberTtl,
      retention = RetentionPolicy.Subscriptions,
      sources,
      watchSourcesLoadingState = true,
    } = options;

    if (clearDelay < 0) {
      throw Error('`clearDelay` must be >= 0');
    }

    RxCached.addInstance(this);
    this.updateLogger(id);

    this._id = id;
    this.cacheRetention = retention;
    this.clearDelay = clearDelay;
    this.dataProducer = dataProducer;
    this.reloadInterval = reloadInterval;
    this.reloadSourcesOnReloadInterval = reloadSourcesOnReloadInterval ?? false;
    this.watchSourcesLoadingState = watchSourcesLoadingState;

    this.data$ = this.getDataCachingObservable();

    this.dataWithError$ = () =>
      merge(
        this.data$,
        this.error$.pipe(
          mergeMap((err) => {
            if (err != null) {
              throw err;
            }

            return EMPTY;
          })
        )
      );

    if (sources) {
      this.log(`Enabling sources support`);

      this._sources = sources;
      this.skippableSources = sources.filter(
        (source) => 'cached' in source && source.skipWhen != null
      ) as SourceDefinition<any>[];
    }

    if (rememberTtl > 0) {
      this.rememberQueue = new AutoClearingQueue<T>(rememberCount ?? Infinity, rememberTtl);
    }

    this.log(
      `Cache created, retention=${retention}${
        retention === RetentionPolicy.Subscriptions ? `, clear-delay=${clearDelay}ms` : ''
      }`
    );
  }

  /**
   * Change the identity and optionally update the data producer. Note that the identity of a cached
   * group cannot be changed as it is a combination of the IDs of all its sources.
   *
   * @param id new cached data ID.
   * @param dataProducer optional new data production function, params are passed if this cached has sources
   * @returns `true` if the ID has changed, meaning the new `dataProducer` was accepted and
   * executed. On `false` nothing happens.
   */
  changeIdentity(id: string, dataProducer?: (params?: any[]) => Observable<T>): boolean {
    if (this._sources?.length > 0) {
      throw new Error(
        'Cannot change the identity of a cached group. The identity of a cached group is the combination of the IDs of all sources.'
      );
    }

    const identityChanged = this._id !== id;

    if (identityChanged) {
      if (this._producingData === true) {
        this.parkedChangeIdentity = () => {
          this.performIdentityChangedActions(id, dataProducer);
        };
      } else {
        this.performIdentityChangedActions(id, dataProducer);
      }
    } else if (this.reloadInterval != null) {
      this.restartAutomaticReload();
    }

    return identityChanged;
  }

  /**
   * Changing identity while a producer function is running would lead to the problem that when the data arrives it
   * would populate the cache after identity change. Consequently, we need to park identity change until producer
   * finishes and then kick off the identity change actions.
   */
  private parkedChangeIdentity: () => void | null = null;

  private performIdentityChangedActions(id: string, dataProducer?: (params?: any[]) => Observable<T>) {
    // first we need to cancel any pending automatic clear of previous data
    this.clearCacheClearingTimeout();
    this.updateLogger(id);

    // then we replace identity and production function and load the data
    this.log(`identity changed to [${id}]`);
    this._id = id;

    if (dataProducer != null) {
      this.dataProducer = dataProducer;
    }

    // clear data from cache
    this.clear(false);

    if (this.hasSubscriptions) {
      // and if there are subscriptions then load it again
      this.dataProducerEvents$.next({ type: ProducerEventType.Load });
    }
  }

  /**
   * Clear the cache.
   *
   * Technically this means emitting `undefined` in `data$` and that is never emitted.
   * The error is cleared as well and loading is set to `true`.
   *
   * @param clearRememberedValues Clear all remembered values as well, by default true.
   */
  clear(clearRememberedValues: boolean = true): void {
    this.log('Clear instance');

    this.dataProducerEvents$.next({
      clearRememberedValues,
      data: undefined,
      type: ProducerEventType.Explicit,
    } satisfies ClearEvent);

    this._cleared$.next();

    if (clearRememberedValues && this.rememberQueue != null) {
      this.rememberQueue.clear();
    }

    this.sourceData = null;
    this.sourceLoadingState = 'initialize';

    this.clearCacheClearingTimeout();
    this.setError(null);
    this.setLoading(true);
  }

  /**
   * Remove the remembered value that matches the given ID.
   * @param id ID of the entry to remove.
   */
  clearRememberedValue(id: string): void {
    this.rememberQueue.delete(id);
  }

  /**
   * Will refresh the cached data by re-executing the `dataProducer` function.
   * Also clears any previously emitted error.
   *
   * @param reloadSources Reload all sources as well, true by default.
   * @param autoReload Indicates that this is an automatic reload, false by default.
   * @returns  `Observable<void>` that emits `undefined` and completes as soon as reload event is done.
   */
  reload(reloadSources: boolean = true, autoReload: boolean = false): Observable<void> {
    // It is necessary to check if the instance is currently producing data because with the change
    // introduced by 9d198051 (https://git.neotel.at/neotel/portal/portal-next/-/commit/9d198051)
    // we now always clear the instance before reloading. This causes the check that happens
    // whenever a new producer event is emitted to fail because the EXPLICIT event clears
    // the value after the current operation finished and then the reload will happen again.
    //
    // This specific case can happen whenever there are multiple
    // cached groups that use the same cached instance as its source.
    if (this.producingData) {
      this.log('Skipping reload, instance is currently producing data');

      return of(undefined);
    } else {
      this.log('Reload instance');

      // Clear instance before reloading so that new subscribers do not receive the current value
      this.dataProducerEvents$.next({
        clearRememberedValues: false,
        clearSources: reloadSources && this.subscriptionCount === 0,
        type: ProducerEventType.Explicit,
      } satisfies ClearEvent);

      // reload if there are subscribers or the retention policy is forever
      if (this.hasSubscriptions || this.cacheRetention === RetentionPolicy.Forever) {
        this.dataProducerEvents$.next({
          autoReload,
          reloadSources,
          type: ProducerEventType.Reload,
        });

        // Wait for loading instead of producing because a group will not immediately set producing to true if it has to wait for sources.
        if (this._loading === true) {
          return this.loading$.pipe(
            first((loading) => loading === false),
            map(() => undefined)
          );
        }
      }

      return of(undefined);
    }
  }

  /**
   * Reload the instance if there are any subscriptions when called. Otherwise,
   * the instance is cleared.
   */
  reloadOrClear(reloadSources: boolean = true): void {
    this.log('Reload or clear instance');

    if (this.hasSubscriptions) {
      this.reload(reloadSources);
    } else {
      this.clear();
    }
  }

  /**
   * Update the cache explicitly. This will also clear any previously emitted error.
   * @param data The data that should be stored in the cache.
   */
  setData(data: T): void {
    if (data === undefined) {
      throw Error('Setting `undefined` to the cache is not allowed.');
    }

    this.dataProducerEvents$.next({
      data,
      type: ProducerEventType.Explicit,
    });
  }

  /**
   * Explicitly suspending auto-reload.
   * Do this in case where you know that the data-producer function cannot produce at the time.
   * Auto-reload resumes when `changeIdentity` is called (regardless if the identity really changed)
   * or on first subscription.
   */
  suspendAutomaticReload(): void {
    this.stopAutomaticReload();
  }

  private initialize() {
    const firstSubscription = this.subscriptionCount === 1;
    const isEmpty = this.cleared === true;
    const errored = this._error != null;
    const hasSources = this._sources != null;
    const hasNoSourceSubscription = this.sourceDataSubscription == null && this.waitingForSources === false;
    const notProducing = this._producingData === false;
    const subscriptionBased = this.cacheRetention === RetentionPolicy.Subscriptions;

    if (firstSubscription) {
      this.log(
        `is-empty=${isEmpty}, errored=${errored}, producing=${!notProducing}, subscription-based=${subscriptionBased}`
      );
    }

    if (firstSubscription && subscriptionBased) {
      // we clear automatic cleanup if "first" new subscription came before timeout
      this.clearCacheClearingTimeout();
      this.stopAutomaticReload();
    }

    if (notProducing && isEmpty) {
      this.log('Load data because a new subscription came in and the instance is empty');
      this.dataProducerEvents$.next({ type: ProducerEventType.Load });
    }

    if (firstSubscription && this.hasActiveSource) {
      this.setLoading(true);
    }

    // If this instance has data it will not subscribe immediately to its sources,
    // which can cause them to clear themselves (if there are no other subscriptions
    // to them). That is why we subscribe to them in this case. If this instance
    // is empty, then it will automatically subscribe to the sources and also emit
    // a LOAD event to them, so in this case we don't need to subscribe here.
    if (hasSources && hasNoSourceSubscription && isEmpty === false) {
      this.subscribeToSources();
    }
  }

  private store(data: T): void {
    if (this.rememberQueue != null) {
      this.rememberQueue.push(data, this.id).then((id) => {
        this.log(`Forgot ${id}`);
      });
    }
  }

  private setError(err: PortalError | null): void {
    this._error = err;
    this._error$.next(err);
  }

  private setLoading(loading: boolean): void {
    this.log('Set loading to', loading);
    this._loading = loading;
    this._loading$.next(loading);
  }

  private setProducing(producing: boolean): void {
    this.log('Set producing to', producing);
    this._producingData = producing;
  }

  private subscribeToSources(): void {
    this.log(`Subscribing to sources`);

    // Map of produced value flags that, so we know which source has emitted and
    // for which source the data was already available.
    const producedValue: Record<string, boolean> = {};

    // Map of flags, so we know which source has been cleared. We reload these
    // instances whenever a source emits values.
    const hasBeenCleared: Record<string, boolean> = {};

    this.sources.forEach((source) => {
      producedValue[source.id] = false;
    });

    const sourceData$: Observable<SourceData | SourceError | SourceCleared>[] = this.sources.map((source) =>
      merge(
        source.data$.pipe(
          map((value): SourceData => {
            return {
              source,
              type: 'data',
              value,
            };
          }),
          tap(() => {
            hasBeenCleared[source.id] = false;
            producedValue[source.id] = true;
          })
        ),
        source.error$.pipe(
          filter((error) => error != null),
          map((error): SourceError => {
            hasBeenCleared[source.id] = false;

            return { type: 'error', error, id: source.id };
          })
        )
      )
    );

    if (this.watchSourcesLoadingState) {
      this.log('Watching sources loading state');

      const sourcesLoading$ = this._sources!.filter((source) => {
        const sourceDefinition = isSourceDefinition(source);

        return sourceDefinition === false || (sourceDefinition && source.ignoreLoadingState !== true);
      }).map((source) => {
        if ('cached' in source) {
          return source.cached.loading$;
        } else {
          return source.loading$;
        }
      });

      this.sourceLoadingSubscription = combineLatest(sourcesLoading$).subscribe((sourcesLoading) => {
        this.log('Sources loading state changed', sourcesLoading);

        if (this.loading === false) {
          const anyLoading = sourcesLoading.reduce((a, b) => a || b, false);

          if (anyLoading) {
            this.log('Some source is loading, marking instance as loading');
            this.setLoading(true);
          }

          if (this.subscriptionCount === 0) {
            this.log('No more subscriptions, clearing instance');

            this.clear();
          }
        }
      });
    }

    // Remember the clear subscription, so we only subscribe once (this instance will not
    // unsubscribe from the clear$ observables of its sources when it unsubscribes from data$
    // and loading$). This way this instance knows when a source was cleared and can act
    // accordingly (even if it has no active subscriptions). We can do this because a source will
    // not act based on subscriptions to cleared$ (which it does for subscriptions to data$).
    if (this.sourceClearSubscription == null) {
      const sourceClear$ = merge(...this.sources.map((source) => source.cleared$.pipe(map(() => source))));

      this.sourceClearSubscription = sourceClear$.subscribe((source) => {
        hasBeenCleared[source.id] = true;

        if (this.cleared === false) {
          this.log(`Source ${source.id} was cleared, clearing this instance`);
          this.clear();
        }
      });
    }

    // We need to wait for all sources to change when the sources are reloaded. Therefore, each
    // time the source emits data, this counter is updated and decremented for each source data
    // emission. When the counter reaches zero, we know that all sources have been reloaded.
    let allChangedCounter = 0;

    this.sourceDataSubscription = combineLatest(sourceData$)
      .pipe(
        tap(() => {
          if (this.sourceLoadingState === 'start-reload') {
            allChangedCounter = this.sources.length;
            this.sourceLoadingState = 'reloading';
          }
        })
      )
      .subscribe({
        next: (sourcesData) => {
          if (
            this.sourceLoadingState === 'done' ||
            this.sourceLoadingState === 'initialize' ||
            (this.sourceLoadingState === 'reloading' && --allChangedCounter === 0)
          ) {
            const someSourceErrored = sourcesData.some(
              (value) => value != null && 'type' in value && value.type === 'error'
            );

            // If a source errored, we need to propagate this error to the error stream of this instance.
            if (someSourceErrored) {
              this.log(`Sources errored`);

              this.waitingForSources = false;
              this.sourceLoadingState = 'done';

              if (this.hasSubscriptions) {
                const errors: PortalError[] = sourcesData
                  .filter((value) => value != null && 'type' in value && value.type === 'error')
                  .map((value: SourceError) => {
                    return {
                      cause: value.error,
                      id: value.id,
                    };
                  });

                const error: SourcesError = {
                  errors,
                  id: this._id,
                  message: 'Some sources errored',
                  name: 'SourceError',
                };

                this.dataProducerEvents$.next({
                  data: error,
                  type: ProducerEventType.SourcesError,
                });
              }
            } else {
              this.log('Received values from sources', sourcesData);

              const sourceDataIsDefined =
                this.cleared === false && this.sourceData?.length === sourcesData.length;

              const sourceDataHasChanged = this.sourceData?.some((data, index) => {
                const event = sourcesData[index];
                return event.type === 'data' && event.value !== data;
              });

              // This instance will subscribe to sources when a new subscription is the
              // first subscription. In this case it will receive data from its sources.
              // If this instance is not empty, then it will still have source data
              // and if it did not change (comparing by reference so sources have to
              // emit the exact same objects), we will ignore the newly received data.
              if (sourceDataIsDefined && sourceDataHasChanged === false) {
                this.log('Ignoring source data because it has not changed');

                this.sources.forEach((source) => {
                  producedValue[source.id] = false;
                });

                if (this.loading) {
                  this.setLoading(false);
                }

                return;
              }

              // Check if a source has been cleared since the last time
              // this instance has received some data from its sources.
              const clearedSources = Object.entries(hasBeenCleared)
                .filter(([, cleared]) => cleared)
                .map(([id]) => this.sources.find((source) => source.id === id));

              // If so, we reload these sources and wait until all of them have emitted again
              if (clearedSources.length > 0) {
                this.log(`Some sources have been cleared, reloading these instances`, clearedSources);

                this.sourceLoadingState = 'reloading';
                allChangedCounter = clearedSources.length;

                clearedSources.forEach((source) => {
                  hasBeenCleared[source.id] = false;
                  source.reload();
                });
              } else {
                const sourceValues = sourcesData as SourceData[];
                this.sourceData = sourceValues.map((source) => source.value);
                this.sourceLoadingState = 'done';
                this.waitingForSources = false;

                this.dataProducerEvents$.next({
                  data: sourceValues.map((data) => {
                    return {
                      emitted: producedValue[data.source.id],
                      id: data.source.id,
                      value: data.value,
                    };
                  }),
                  type: ProducerEventType.Sources,
                });

                this.sources.forEach((source) => {
                  producedValue[source.id] = false;
                });
              }
            }
          }
        },
      });
  }

  private tearDown() {
    if (this.subscriptionCount === 0) {
      this.stopAutomaticReload();

      // Unsubscribe immediately from sources when the last subscription is
      // removed. This causes sources to unsubscribe from their sources as well
      // and clears sourceData in this instance.
      this.unsubscribeFromSources();

      if (this.loading && this.state !== CachedState.Empty) {
        this.setLoading(false);
      }
    }

    // clear cached data in case ...
    if (
      this._error == null &&
      this.subscriptionCount === 0 &&
      this.cacheRetention === RetentionPolicy.Subscriptions &&
      this.cleared === false
    ) {
      this.log(`Setting cache clear timeout in ${this.clearDelay}ms`);

      this.cacheClearTimeoutID = setTimeout(() => {
        this.log(
          'Clearing cache due to zero subscriptions and elapsed delay time of',
          `${this.clearDelay}ms`
        );

        this.clearCache();
      }, this.clearDelay);
    }

    // when subscriptions drop to zero and last data production got an error then set loading state to
    // `true` for next subscriber, because the next subscription will explicitly produce `Load` action.
    // (omits `loading$` flag flapping for first subscriber).
    if (this._error != null && this.subscriptionCount === 0) {
      this.setError(null);
      this.setLoading(true);

      this._cleared$.next();
    }
  }

  private clearCache(): void {
    this.dataProducerEvents$.next({
      clearRememberedValues: true,
      data: undefined,
      type: ProducerEventType.Explicit,
    });

    this.rememberQueue?.clear();

    this.cleared = true;
    this.sourceData = null;
    this._cleared$.next();

    this.unsubscribeFromSources();

    this.cacheClearTimeoutID = undefined;
  }

  /**
   * Cancel the automatic clearing of previously cached data.
   */
  private clearCacheClearingTimeout(): void {
    if (this.cacheClearTimeoutID != null) {
      this.log('Clear timeout ID', this.cacheClearTimeoutID);

      clearTimeout(this.cacheClearTimeoutID);
      this.cacheClearTimeoutID = undefined;
    }
  }

  // Enter the rxjs magic ;-)
  private getDataCachingObservable(): Observable<T> {
    const dataCachingObservable = merge(
      // Initially we want to load/produce the data and cache it immediately.
      // Hence, merging `Load` event with `_dataProducerEvents$`
      of({ type: ProducerEventType.Load } as ProducerEvent<T>),
      this.dataProducerEvents$
    ).pipe(
      tap((event) => {
        this.log('Received event', event.type);

        const isReload = event.type === ProducerEventType.Reload;
        const isClearThatShouldClearRememberedValues = isClearEvent(event) && event.clearRememberedValues;
        const isClearThatShouldClearSources = isClearEvent(event) && event.clearSources;

        if (isReload) {
          this.stopAutomaticReload();
        }

        if (this.rememberQueue != null && (isReload || isClearThatShouldClearRememberedValues)) {
          this.log('Clear active remembered value');
          this.rememberQueue.clearActive();
        }

        if (isClearThatShouldClearSources) {
          this.log('Clear sources');
          this._sources?.forEach((source) => {
            const cached = isSourceDefinition(source) ? source.cached : source;

            if (cached.hasSubscriptions === false) {
              cached.clear(isClearThatShouldClearRememberedValues);
            }
          });
        }
      }),

      // Do not process `Reload` events as long we are "producing"
      filter((event) => {
        if (event.type === ProducerEventType.Reload && this._producingData === true) {
          this.log(
            'Ignore event',
            event.type,
            'due to producing',
            this._producingData,
            ', is loading',
            this._loading
          );
          return false;
        }

        return true;
      }),

      map((event) => {
        let skip = false;

        if (this.sources != null) {
          const emptySources = this.sources.filter((source) => source.state === CachedState.Empty);
          const hasEmptySource = emptySources.length > 0;
          const hasProducingSources = this.sources.some(
            (source) =>
              source.state === CachedState.Producing || source.state === CachedState.WaitingForSources
          );
          const isLoadEvent = event.type === ProducerEventType.Load;
          const isReloadEvent = event.type === ProducerEventType.Reload;
          const subscribeToSources = this.sourceDataSubscription == null && this.waitingForSources === false;

          if ((isLoadEvent || isReloadEvent) && (subscribeToSources || hasEmptySource)) {
            if (subscribeToSources) {
              this.log(`Subscribe to sources and skip event`, event);

              // If the cached object has just been created, there is no subscription to
              // this._dataProducerEvents$ yet. If the sources are already available, then the SOURCES
              // event will be emitted immediately, which causes it to get lost (because
              // _dataProducerEvents$ has not been subscribed to yet). Therefore, we wait for one
              // js cycle before subscribing to the sources.
              setTimeout(() => {
                this.subscribeToSources();
              }, 0);
            } else {
              // We need to wait for one cycle because it could be that the
              // empty sources have no subscriptions and if that is the case,
              // then it is necessary to subscribe to the sources before the
              // RELOAD event is emitted. Otherwise, the source would try to
              // reload itself before it has been initialized and this would
              // result in an error.
              setTimeout(() => {
                // The sources that were empty before could now be already producing. We do not want to reload them again.
                const sourcesToReload = this.sources.filter((source) => source.state === CachedState.Empty);

                if (sourcesToReload.length > 0) {
                  this.log(
                    'Reload previously cleared sources that are still empty',
                    sourcesToReload.map((source) => source.id)
                  );

                  sourcesToReload.forEach((source) => source.reload(false));
                }
              }, 0);
            }

            skip = true;
            this.waitingForSources = true;
          } else if ((isLoadEvent || isReloadEvent) && hasProducingSources) {
            const notProducingSources = this.sources.filter((source) => {
              source.state;
              return source.state === CachedState.HasValue;
            });

            // on reload event (with `reloadSources===true`) we need to ensure that all sources get reloaded
            if (
              event.type === ProducerEventType.Reload &&
              event.reloadSources &&
              notProducingSources.length > 0
            ) {
              this.log(
                `Only some sources are producing currently. Reloading these sources and ignoring the event ${event.type}.`,
                notProducingSources.map((source) => source.id)
              );

              const ev = event as ReloadProducerEvent;
              notProducingSources.forEach((s) => s.reload(ev.reloadSources, ev.autoReload));
            } else {
              this.log(`Ignoring ${event.type} event because some sources are currently producing`);
            }

            skip = true;
          } else if (event.type === ProducerEventType.Sources && this.subscriptionCount === 0) {
            this.log(`Clearing this instance because sources emitted but there are no active subscriptions`);

            event = {
              clearRememberedValues: false,
              type: ProducerEventType.Explicit,
            } satisfies ClearEvent;
          } else if (event.type === ProducerEventType.Sources && this.hasActiveSource) {
            this.log(
              `Skip sources event because some sources are currently producing data`,
              this.sources.filter((source) => source)
            );

            skip = true;
          } else if (event.type === ProducerEventType.Reload && event.reloadSources) {
            const ev = event as ReloadProducerEvent;
            this.log(`Reload sources and skip event`, this.sources);

            skip = true;
            this.sourceLoadingState = 'start-reload';
            this.waitingForSources = true;
            this.sources.forEach((source) => source.reload(ev.reloadSources, ev.autoReload));
            if (ev.autoReload === false) {
              this.setLoading(true);
            }
          }
        }

        return skip ? null : event;
      }),

      filter((event: ProducerEvents<T> | null) => {
        let pass = true;

        if (event == null) {
          pass = false;
        } else {
          const isLoad = event.type === ProducerEventType.Load;
          const isSourceData = event.type === ProducerEventType.Sources;
          const isSource = isSourceData || event.type === ProducerEventType.SourcesError;
          const isReload = event.type === ProducerEventType.Reload;

          if (this.waitingForSources && isSource === false) {
            this.log(`Skipping ${event.type} because sources are producing data`);

            pass = false;
          } else if ((isLoad || isReload || isSource) && this._producingData) {
            this.log(`Skipping ${event.type} because data is being produced`);

            pass = false;
          } else if (this.waitingForSources === false && isSourceData && this.loading === false) {
            const sourceEvent = (event as SourcesProducerEvent).data;
            const changedSources = sourceEvent.filter((data) => data.emitted);
            const hasSkippableSources: boolean = this.skippableSources.length > 0;

            // Check if any source has changed that is not ignorable
            const notIgnorableSourcesChanged = changedSources.some((sourceData) => {
              const maybeSource = this.skippableSources.find((source) => source.cached.id === sourceData.id);

              return maybeSource == null;
            });

            // only perform skippable sources logic if the cached was not cleared, otherwise we might
            // get stuck in 'waitingForSources' when the only source is a skippable source (when the cached is cleared).
            if (this.cleared === false && hasSkippableSources && notIgnorableSourcesChanged === false) {
              this.log(`Ignorable source found, checking`);

              const ignores = this.skippableSources.map(({ cached, skipWhen }) => {
                const rxCached = cached as RxCached<any>;

                if (rxCached.producedData != null && rxCached.previousData != null) {
                  return skipWhen(rxCached.producedData, rxCached.previousData);
                } else {
                  return false;
                }
              });

              if (ignores.some((ignore) => ignore === false)) {
                this.log(`Not ignoring as some source has changed`);
              } else {
                this.log(`Ignoring source data, because no data has changed`);
                pass = false;
              }
            }
          }
        }

        return pass;
      }),

      // Here we switch the event to data production observable
      mergeMap((event: ProducerEvents<T>) => {
        this.log('Producing data because of', event.type, event);

        this.setProducing(true);

        if (event.type !== ProducerEventType.SourcesError) {
          // in case auto reload is in progress we do not emit that data is reloading
          if (event.type === ProducerEventType.Reload && event.autoReload) {
            // no loading event
          } else if (event.type !== ProducerEventType.Explicit) {
            if (this.rememberQueue != null) {
              this.setLoading(this.rememberQueue.has(this.id) === false);
            } else {
              this.setLoading(true);
            }
          }
        }

        let producedData$: Observable<T>;

        if (event.type === ProducerEventType.Explicit) {
          producedData$ = of(event.data as T);
        } else {
          let data$: Observable<T>;

          try {
            if (event.type === ProducerEventType.Sources) {
              data$ = this.dataProducer(event.data.map((sourceData) => sourceData.value));
            } else if (event.type === ProducerEventType.SourcesError) {
              data$ = throwPortalError({
                cause: event.data,
                id: this.id,
              });
            } else if (this.sources != null) {
              const rememberedData$ = this.rememberQueue?.retrieve$(this.id)?.pipe(delay(0));

              if (rememberedData$ != null) {
                data$ = rememberedData$;
              } else if (this.sourceData != null) {
                data$ = this.dataProducer(this.sourceData);
              } else {
                // This should not happen, however I have seen something like
                // and cannot reproduce it. That's why I have implemented this
                // error message, so in case it does happen, it is clear what
                // happened by reading the error message (otherwise there would
                // be a generic error stating that undefined cannot be spread,
                // which is not directly understandable).
                data$ = throwPortalError({
                  cause: new Error('Trying to call data producer, but source data is missing.'),
                  id: 'cached:source-data-is-missing',
                });
              }
            } else {
              const rememberedData$ = this.rememberQueue?.retrieve$(this.id)?.pipe(delay(0));

              if (rememberedData$ != null) {
                this.log('Using remembered data');
              }

              data$ = rememberedData$ ?? this.dataProducer();
            }
          } catch (e) {
            this.logger.error(`[${this.id}] data producer caused error`, e);

            data$ = throwPortalError({
              cause: e,
              id: this.id,
              i18n: 'Data producer caused error',
            });
          }

          producedData$ = data$.pipe(
            // whatever the observable from `dataProducer` does, we only take one value from it.
            take(1),
            // clear errors only if the `dataProducer` emits successfully
            tap(() => {
              this.setError(null);
            }),
            // TODO making data immutable
            // map(deepFreeze),
            // we do not want to complete the source caching the data. hence, we handle errors.
            catchError((err: any, caught: Observable<T>) => {
              this.logger.error(`[${this.id}]`, event.type, 'error:', err, ', caught in source', caught);

              this.setProducing(false);
              this.setError(err);
              this.setLoading(false);

              if (this.reloadInterval != null) {
                this.restartAutomaticReload();
              }

              // on error, we do not emit any data for new subscribers
              // this simply sets cached data to undefined value and the
              // `undefined` is not emitted to subscribers.
              return of(undefined as unknown as T);
            })
          );
        }

        return producedData$;
      }),
      tap((data) => {
        if (data != null) {
          this.store(data);
        }
      }),
      tap((producedData) => {
        this.setProducing(false);
        this.previousData = this.producedData;
        this.producedData = producedData;

        this.log('New data:', producedData);

        // only if `producedData` is not undefined set the loading state to false
        if (producedData !== undefined) {
          this.setLoading(false);

          if (this.reloadInterval != null) {
            this.restartAutomaticReload();
          }

          if (this.cleared) {
            this.cleared = false;
          }
          if (this.parkedChangeIdentity != null) {
            this.parkedChangeIdentity();
            this.parkedChangeIdentity = null;
          }
        } else {
          this.cleared = true;
        }
      }),
      // Share the observable with all subscribers and also replay the last produced data to new subscribers.
      shareReplay(1),
      // Cached data undefined? We do not emit that.
      filter((producedData) => producedData !== undefined)
    );

    return this.subscriberWithCount(dataCachingObservable);
  }

  private restartAutomaticReload() {
    this.stopAutomaticReload();
    this.startAutomaticReload();
  }

  private startAutomaticReload(): void {
    if (this.reloadInterval == null) {
      throw new Error('Cannot start the reload interval because `reloadInterval` is not defined');
    }

    if (this.hasSubscriptions) {
      this.log(`Starting reload interval with timeout of ${this.reloadInterval}ms`);

      this.reloadTimeoutID = setTimeout(() => {
        if (this.hasSubscriptions) {
          this.log(`Emitting reload event after timeout has passed`);

          this.dataProducerEvents$.next({
            autoReload: true,
            reloadSources: this.reloadSourcesOnReloadInterval,
            type: ProducerEventType.Reload,
          });

          this.reloadTimeoutID = null;
        } else {
          this.log(`Not emitting reload, no more subscriptions`);
        }
      }, this.reloadInterval);
    }
  }

  private stopAutomaticReload(): void {
    if (this.reloadTimeoutID != null) {
      clearTimeout(this.reloadTimeoutID);
      this.reloadTimeoutID = null;

      this.log(`Stopped automatic reload`);
    }
  }

  /**
   * Tracking subscriptions count and
   * re-producing data on new subscription in case `error != null` or data was cleared.
   * In case of `CacheRetention.Subscriptions` and on zero subscriptions we clear the data from
   * `data$` and load data again on the next first subscription.
   */
  private subscriberWithCount<R = T>(sourceObservable: Observable<R>): Observable<R> {
    if (environment.production === false && this.log != null) {
      this.log('Subscribed from', getCallerName());
    }

    return new Observable((subscriber: Subscriber<R>) => {
      this.subscriptionCount++;
      this._hasSubscriptions$.next(true);

      const subscription = sourceObservable.subscribe(subscriber);
      this.log('Subscriptions:', this.subscriptionCount);

      this.initialize();

      // auto-reload must be reactivated if it is not already active whenever first subscriber comes (by checking `reloadTimeoutID`)
      if (this.subscriptionCount === 1 && this.reloadInterval != null && this.reloadTimeoutID == null) {
        this.startAutomaticReload();
      }

      return () => {
        subscription.unsubscribe();
        this.subscriptionCount--;
        this._hasSubscriptions$.next(this.subscriptionCount > 0);
        this.log('Subscriptions:', this.subscriptionCount);

        this.tearDown();
      };
    });
  }

  private unsubscribeFromSources(): void {
    if (this.sourceDataSubscription != null) {
      this.log(`Unsubscribing from sources`);

      this.sourceDataSubscription?.unsubscribe();
      this.sourceDataSubscription = null;
      this.sourceLoadingSubscription?.unsubscribe();
      this.sourceLoadingSubscription = null;
      this.waitingForSources = false;
      this.sourceLoadingState = 'initialize';
    }
  }

  private log$: Subject<any>;

  private updateLogger(id: string) {
    const debug = this.logger.debug.bind(this.logger.debug, `[${id}]`);

    if (RxCached.recordEvents) {
      this.log$ ??= new ReplaySubject();

      this.log = (...args) => {
        debug(...args);

        const date = new Date();
        args.unshift(
          date.toTimeString().slice(0, 8) + '.' + date.getMilliseconds().toString().padStart(3, '0')
        );

        this.log$.next(
          args
            .filter((arg) => {
              return arg instanceof RxCached === false;
            })
            .map((arg) => {
              try {
                return typeof arg !== 'symbol' && typeof arg !== 'number' && typeof arg !== 'string'
                  ? JSON.stringify(arg).slice(0, 100)
                  : arg;
              } catch {
                return '';
              }
            })
            .join(' ')
        );
      };
    } else {
      this.log = debug;
    }
  }
}

// Different data production reasons
enum ProducerEventType {
  /** Initial data production, happens on initialization or after automatic clearing of cached data. */
  Load = 'LOAD',
  /** Event used by calling `reload()` */
  Reload = 'RELOAD',
  /** Event for explicitly setting a data into the cache without consulting data production function. */
  Explicit = 'EXPLICIT',
  /** Sources emitted new value */
  Sources = 'SOURCES',
  /** At least one source emitted an error */
  SourcesError = 'SOURCES_ERROR',
}

interface ProducerEvent<T> {
  data?: T;
  type: ProducerEventType.Load | ProducerEventType.Explicit;
}

interface ClearEvent {
  clearRememberedValues: boolean;
  clearSources?: boolean;
  data?: undefined;
  type: ProducerEventType.Explicit;
}

interface ReloadProducerEvent {
  // if initiated by `reloadInterval`, hence auto reload
  autoReload: boolean;
  reloadSources: boolean;
  type: ProducerEventType.Reload;
}

interface SourcesErrorProducerEvent {
  data: SourcesError;
  type: ProducerEventType.SourcesError;
}

interface SourcesProducerEvent {
  data: { emitted: boolean; id: string; value: any }[];
  type: ProducerEventType.Sources;
}

interface SourcesError extends Error {
  errors: PortalError[];
  id: string;
}

interface SourceData {
  source: Cached<any>;
  type: 'data';
  value: any;
}

interface SourceError {
  error: PortalError;
  id: string;
  type: 'error';
}

interface SourceCleared {
  source: Cached<any>;
  type: 'cleared';
}

type ProducerEvents<T> =
  | ProducerEvent<T>
  | ReloadProducerEvent
  | SourcesErrorProducerEvent
  | SourcesProducerEvent
  | ClearEvent;

export enum CachedState {
  Empty = 'Empty',
  WaitingForSources = 'WaitingForSources',
  Producing = 'Producing',
  Errored = 'Errored',
  HasValue = 'HasValue',
}

registerWithGlobalApp('rxCached', RxCached);

function getCallerName(): string {
  const prepareStackTrace = Error.prepareStackTrace;
  Error.prepareStackTrace = (error: Error, callSites: NodeJS.CallSite[]): string => {
    const errorString = Error.prototype.toString.call(error);
    if (callSites.length === 0) return errorString;

    const appCallSites = callSites
      .filter(
        (callSite) =>
          callSite.getFileName() != null &&
          callSite.getFileName().includes('vendor') === false &&
          callSite.getFileName().includes('polyfills') === false
      )
      .join('\n    at ');

    return `${errorString}\n    at ${appCallSites}`;
  };

  const stack = new Error().stack;

  Error.prepareStackTrace = prepareStackTrace;

  return stack
    .split('\n')
    .map((x) =>
      x
        .trim()
        .replace(/^at\s*/, '')
        .replace(/\s\(.*$/, '')
        .trim()
    )
    .filter(
      (x) =>
        /^([A-Z]|get |new (^RxCached))/.test(x) &&
        /^(RxCached|Error|.*Subscriber|.*Object\..*_Factory|.*Injector).*/.test(x) === false
    )
    .map((x) => x.replace(/ \[as .*]/, ''))
    .reverse()
    .join(', ');
}

function isSourceDefinition(source: Cached<any> | SourceDefinition<any>): source is SourceDefinition<any> {
  return 'cached' in source;
}

function isClearEvent(event: any): event is ClearEvent {
  return (
    event.type === ProducerEventType.Explicit && event.data === undefined && 'clearRememberedValues' in event
  );
}
